import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ScreenPickerModal — rendered once by PopoutProvider when a pop-out
// request needs the user to choose a target display. Never mounted
// directly by consumers.
//
// Layout is a horizontal row of screen cards proportional to each
// screen's physical width × height, with a min-size floor so a small
// laptop screen next to a 4K external remains clickable. Clicking a
// card immediately triggers the pop-out and closes the modal — there
// is no separate OK button. Cancel leaves everything untouched.
//
// Imported inline to keep the modal self-contained and avoid coupling
// it to the terminal's color constants. The terminal palette is
// duplicated here; any refactor that unifies these belongs in its
// own commit.
const C = {
  bg: 'rgba(5, 8, 16, 0.85)',        // backdrop
  card: '#0f1420',
  border: '#1e2a3f',
  borderActive: '#ff6b00',
  text: '#c8d4e8',
  textDim: '#8899b4',
  accent: '#ff6b00',
  accentBg: 'rgba(255,107,0,0.12)',
};

const MIN_CARD_W = 80;
const MIN_CARD_H = 60;
const MAX_CARD_DIM = 280;

const labelFor = (screen, idx, t) => {
  if (screen.label) return screen.label;
  if (screen.isPrimary) return t('terminal.popout.screenPickerPrimary');
  return t('terminal.popout.screenPickerDisplay', { n: idx + 1 });
};

const ScreenCard = ({ screen, idx, scale, onClick, t }) => {
  const w = Math.max(MIN_CARD_W, Math.round(screen.width * scale));
  const h = Math.max(MIN_CARD_H, Math.round(screen.height * scale));
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: w, height: h, padding: 0,
        background: C.card,
        border: `2px solid ${C.border}`,
        borderRadius: 4,
        color: C.text,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 11,
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column',
        gap: 4,
        transition: 'border-color 0.15s, background 0.15s',
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = C.borderActive;
        e.currentTarget.style.background = C.accentBg;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = C.border;
        e.currentTarget.style.background = C.card;
      }}
    >
      <span style={{ fontWeight: 700, color: C.text }}>
        {labelFor(screen, idx, t)}
      </span>
      <span style={{ fontSize: 9, color: C.textDim, letterSpacing: 0.5 }}>
        {screen.width}×{screen.height}
      </span>
    </button>
  );
};

const ScreenPickerModal = ({ screens, onSelect, onCancel }) => {
  const { t } = useTranslation();
  const [dontAskAgain, setDontAskAgain] = useState(false);

  // Compute one scale factor across all screens so proportions stay
  // honest — the largest display fits MAX_CARD_DIM, everything else
  // scales with it, nothing goes below the MIN_CARD_* floor.
  const scale = useMemo(() => {
    if (!screens?.length) return 1;
    const maxDim = Math.max(...screens.map((s) => Math.max(s.width, s.height)));
    return maxDim > 0 ? MAX_CARD_DIM / maxDim : 1;
  }, [screens]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('terminal.popout.screenPickerTitle')}
      style={{
        position: 'fixed', inset: 0,
        background: C.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 99999,
        fontFamily: "'JetBrains Mono', monospace",
      }}
      onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
    >
      <div
        style={{
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: 6,
          padding: 24,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <div style={{
          fontSize: 14, fontWeight: 700, color: C.text,
          letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6,
        }}>
          {t('terminal.popout.screenPickerTitle')}
        </div>
        <div style={{
          fontSize: 11, color: C.textDim, marginBottom: 16, maxWidth: 480,
        }}>
          {t('terminal.popout.screenPickerDescription')}
        </div>

        <div style={{
          display: 'flex', gap: 12, alignItems: 'flex-end',
          marginBottom: 20, flexWrap: 'wrap',
        }}>
          {screens.map((screen, idx) => (
            <ScreenCard
              key={idx}
              screen={screen}
              idx={idx}
              scale={scale}
              t={t}
              onClick={() => onSelect(screen, { dontAskAgain })}
            />
          ))}
        </div>

        <label style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 11, color: C.textDim, cursor: 'pointer',
          marginBottom: 16,
        }}>
          <input
            type="checkbox"
            checked={dontAskAgain}
            onChange={(e) => setDontAskAgain(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          {t('terminal.popout.screenPickerDontAsk')}
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '6px 14px',
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.textDim,
              fontFamily: 'inherit',
              fontSize: 11,
              letterSpacing: 0.5,
              cursor: 'pointer',
              borderRadius: 3,
            }}
          >
            {t('terminal.popout.screenPickerCancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScreenPickerModal;
